import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import _ from "lodash";
import {getFilterByKey} from "../../store/selectors/filter.selector";
import {take} from "rxjs";
import {DataService} from "../../services/data.service";
import {Store} from "@ngrx/store";
import {AppState} from "../../store";
import {DisplayService} from "../../services/display.service";
import {setJobFilter} from "../../store/actions/filter.actions";
import {DistrictsService} from "../../services/districts.service";
import {District} from "../../interfaces/district";
import {SearchLocationFilterComponent} from "./search-location-filter/search-location-filter.component";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {HttpClient} from "@angular/common/http";

@UntilDestroy()
@Component({
  selector: 'brt-search-location',
  templateUrl: './search-location.component.html',
  styleUrls: ['./search-location.component.scss']
})
export class SearchLocationComponent {
  @ViewChild('mainEl') mainEl!: ElementRef;
  @ViewChild('searchEl') searchEl!: ElementRef;
  @ViewChildren('slFilter') slFilters!: QueryList<SearchLocationFilterComponent>;
  @Output('onClosed') onClosed = new EventEmitter<boolean>();
  @Input('entity') set setEntity(val: "provider" | "job") {
    this.currentEntity = val;

    if(this.currentEntity === "provider") {
      this.settingsKey = "searchTermProviderLocation";
      this.userLocationFilterKey = "userLocationProvider";
      this.districtsFilterKey = "activeProviderDistrictIds";
      this.distanceFilterKey = "providersDistance";
    } else if(this.currentEntity === "job") {
      this.settingsKey = "searchTermJobLocation";
      this.userLocationFilterKey = "userLocationJob";
      this.districtsFilterKey = "activeJobDistrictIds";
      this.distanceFilterKey = "jobsDistance";
    }

    this.store.select(getFilterByKey(this.settingsKey)).pipe(take(1)).subscribe(response => {
      if (response !== null) {
        this.searchValue = response;
        this.storeSearchValue = response;
      }
    });
  }

  cachedAssetsCities: any[] = [];

  currentEntity: "provider" | "job" = "provider";
  elementId = '';
  label = '';
  settingsKey = '';
  userLocationFilterKey = "";
  districtsFilterKey = "";
  distanceFilterKey = "";
  searchValue = "";
  storeSearchValue = "";

  typeDisabled = false;

  suggestionsType: SuggestionsType = SuggestionsType.NONE;
  SuggestionsType = SuggestionsType;

  actionIconType: 'submit' | 'clear' = 'clear';

  isMobileOverlayOpened = false;
  debouncedSearchValueUpdateTrigger = _.debounce(() => this.onSearchValueChanged(), 200);

  activeDistricts: District[] = [];
  savedDistricts: District[] = [];

  googleApiLoading = false;

  outsideComponentClickListener = this.onOutsideComponentClick.bind(this);

  googleMaps: any;
  placesService: any;
  proposedPlaces: {
    lat: number,
    lng: number,
    name: string
  }[] = new Array(0);


  constructor(
    private data: DataService,
    private store: Store<AppState>,
    private display: DisplayService,
    private cdRef: ChangeDetectorRef,
    private httpClient: HttpClient
  ) {
    this.elementId = Math.random().toString(36).substring(7);

    if (window?.google) {
      this.googleMaps = window.google;
      this.placesService = new window.google.maps.places.Autocomplete(document.createElement('input'));
    }

  }

  ngOnInit(): void {
    this.store.select(getFilterByKey(this.settingsKey)).pipe(untilDestroyed(this)).subscribe(response => {
      if (response !== null) {
        this.storeSearchValue = response;
        if(this.savedDistricts.length === 0) {
          this.searchValue = response;
        }
      }
    });
  }


  onSearchValueChanged(): void {

    if (this.settingsKey === '') {
      return;
    }

    if(this.display.isMobileScreenSize()) {
      this.actionIconType = 'clear';
    } else {
      this.actionIconType = 'submit';
    }


    if(this.searchValue.length) {
      this.getPlacesFromGoogle();
      this.showAddressSuggestions();
    } else {
      this.proposedPlaces = [];
      this.showDistrictSuggestions();

      if(this.storeSearchValue.length) {
        this.fullResetLocation();
      }
    }
  }

  setSearchValue(val: string) {
    this.searchValue = val;
    this.storeSearchValue = val;
    this.actionIconType = 'clear';
    this.debouncedSearchValueUpdateTrigger();
  }

  onSimilarItemClicked(parsedPlace: {
    lat: number,
    lng: number,
    name: string
  }) {
    this.hideSuggestions();
    this.actionIconType = 'clear';
    // this.suggestionModalOpen = false;
    this.closeMobileOverlay();
    document.body.removeEventListener("click", this.outsideComponentClickListener);
    this.store.dispatch(setJobFilter({key: this.districtsFilterKey, value: []}));
    this.store.dispatch(setJobFilter({key: this.settingsKey, value: parsedPlace.name}));
    this.store.dispatch(setJobFilter({key: this.userLocationFilterKey, value: {lat: parsedPlace.lat, lng: parsedPlace.lng}}));

    this.store.select(getFilterByKey(this.distanceFilterKey)).pipe(take(1)).subscribe((currentDistance) => {
      if(currentDistance === 0) {
        this.store.dispatch(setJobFilter({key: this.distanceFilterKey, value: 30}));
      }
    });
  }

  closeMobileOverlay() {
    if(this.display.isMobileScreenSize()) {
      this.isMobileOverlayOpened = false;
      this.display.enableBodyScroll();
      this.display.removeElementFillVVHeight(this.mainEl.nativeElement);
    }
  }


  openMobileOverlay() {
    // this.suggestionModalOpen = true;
    if(this.display.isMobileScreenSize()) {
      this.isMobileOverlayOpened = true;
      this.display.disableBodyScroll([this.mainEl.nativeElement], true);
      this.display.addElementFillVVHeight(this.mainEl.nativeElement);
    }
  }

  onInputKeyUp($event: KeyboardEvent) {
    // console.log($event);

    if($event.key === "Enter") {
      this.submitAddressSearch();
    }

  }

  async getPlacesFromGoogle() {
    this.googleApiLoading = true;

    if(this.cachedAssetsCities.length === 0) {
      this.cachedAssetsCities = Array.from(await import("./../../../assets/cities_lat_lng.json"));
    }

    const searchValueLowerCase = this.searchValue.toLowerCase();

    let filteredLocations = this.cachedAssetsCities.filter(el => el.name.toLowerCase().indexOf(searchValueLowerCase) !== -1);

    const searchValueLength = this.searchValue.length;
    filteredLocations.sort((a, b) => {
      if(a.name.length === searchValueLength) return -1;
      if(b.name.length === searchValueLength) return 1;
      return Math.abs(a.name.length - searchValueLength) <= Math.abs(b.name.length - searchValueLength) ? -1 : 1
    })

    filteredLocations = filteredLocations.slice(0, 10);

    this.proposedPlaces = new Array(0);

    this.proposedPlaces = [...filteredLocations];

    if(this.proposedPlaces.length === 0) {
      this.actionIconType = 'clear';
    }

    this.cdRef.detectChanges();

    this.googleApiLoading = false;

    // console.log(filteredLocations);

    // const request = {
    //   query: this.searchValue,
    //   region: "at",
    //   language: "de",
    //   location: {lat: 47.292514, lng: 11.8687441},
    //   radius: 10000,
    //   types: ["locality"]
    // };
    //
    // this.placesService.textSearch((request), (results: any, status: any) => {
    //   if (status === google.maps.places.PlacesServiceStatus.OK) {
    //     let proposedPlaces = this.proposedPlaces;
    //     proposedPlaces = [];
    //     // this.proposedPlaces = new Array(0);
    //     results.forEach((result: any) => {
    //       const parsedPlace = {
    //         lat: result.geometry.location.lat(),
    //         lng: result.geometry.location.lng(),
    //         name: result.name
    //       };
    //       proposedPlaces.push(parsedPlace);
    //     });
    //     this.proposedPlaces = [...proposedPlaces];
    //     this.cdRef.detectChanges();
    //     // console.warn("Updated proposed places:", this.proposedPlaces.length, this.searchValue);
    //   } else if(status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
    //     // console.warn("ZERO RESULTS");
    //     this.proposedPlaces = [];
    //     this.actionIconType = 'clear';
    //   }
    //   this.googleApiLoading = false;
    // });
  }

  submitAddressSearch() {
    if(this.searchValue.length > 0) {
      if(!this.googleApiLoading) {
        if(this.proposedPlaces.length) {
          this.onSimilarItemClicked(this.proposedPlaces[0]);
        }
      }
    }
    const searchHTMLInput = this.searchEl.nativeElement as HTMLInputElement;
    searchHTMLInput.blur();
    document.body.removeEventListener("click", this.outsideComponentClickListener);
    this.hideSuggestions();
    this.closeMobileOverlay();
  }

  districtsValueChanged($event: District[]) {
    this.activeDistricts = $event;
    this.typeDisabled = this.activeDistricts.length > 0;
    this.searchValue = this.activeDistricts.map((el) => el.name).join(", ");
    // console.log("UPDATE IN DISTRICTS VALUE CHANGED");
    if(this.activeDistricts.length === 0 && this.storeSearchValue.length > 0) {
      this.searchValue = this.storeSearchValue;
    }

    if(this.searchValue.length) {
      this.actionIconType = 'clear';
    }
  }

  clearDistricts() {
    this.store.dispatch(setJobFilter({key: this.districtsFilterKey, value: []}));
    if(this.slFilters) {
      this.slFilters.forEach((el) => { el.resetFilter(); });
    }
    this.typeDisabled = false;
  }

  filterByDistricts(districts: District[]) {

    if(districts.length) {
      // console.log("SETTING to zer0");
      this.fullResetLocation();
    }

    // if(this.storeSearchValue.length === 0) {
      this.activeDistricts = districts;
      this.savedDistricts = districts;
      this.districtsValueChanged(districts);
    // }
    this.hideSuggestions();
  }

  /** Reset Term & Location (not districts) */
  fullResetLocation() {
    this.store.dispatch(setJobFilter({key: this.settingsKey, value: ""}));
    this.store.dispatch(setJobFilter({key: this.userLocationFilterKey, value: {lat: 0, lng: 0}}));
  }

  setUserLocation($event: string) {
    this.searchValue = $event;
    if(this.searchValue.length) this.actionIconType = 'clear';
  }

  showAddressSuggestions() {
    this.suggestionsType = SuggestionsType.ADDRESS;
    document.body.removeEventListener("click", this.outsideComponentClickListener);
    document.body.addEventListener("click", this.outsideComponentClickListener);
  }

  showDistrictSuggestions() {
    this.suggestionsType = SuggestionsType.DISTRICT;
    document.body.removeEventListener("click", this.outsideComponentClickListener);
    document.body.addEventListener("click", this.outsideComponentClickListener);
  }

  onOutsideComponentClick($event: MouseEvent) {
    const target = $event.target as HTMLElement;
    if(!target.classList.contains("search-location") && !target.closest(".search-location")) {
      this.hideSuggestions();

      // this.filterByDistricts(this.activeDistricts);
      this.store.dispatch(setJobFilter({key: this.districtsFilterKey, value: this.activeDistricts.map((el) => el.id)}));
      // if(this.activeDistricts.length || this.savedDistricts.length) {
      //   console.log(this.activeDistricts);
      //   console.log(this.savedDistricts);
      //   this.searchValue = this.savedDistricts.map((el) => el.name).join(", ");
      //
      //   this.typeDisabled = this.savedDistricts.length > 0;
      // }

      // if(this.savedDistricts.length === 0) {
      //   if(this.storeSearchValue.length) {
      //     this.searchValue = this.storeSearchValue;
      //   }
      // }
      this.onFocusOut();

      document.body.removeEventListener("click", this.outsideComponentClickListener);
    }
  }

  hideSuggestions() {
    this.suggestionsType = SuggestionsType.NONE;

    if(this.slFilters) {
      this.slFilters.forEach((el) => {
        el.resetNotSavedDistricts();
      });
    }
    // console.warn("hideDistrictSuggestions");

  }

  onFocusIn() {
    this.openMobileOverlay();
    if(this.storeSearchValue.length) this.showAddressSuggestions();
    else this.showDistrictSuggestions();
  }

  onClearIconClicked($event: MouseEvent) {
    if(this.activeDistricts.length) {
      this.clearDistricts();
    }
    if(this.searchValue.length > 0) {
      this.setSearchValue("");
      this.store.dispatch(setJobFilter({key: this.settingsKey, value: ""}));
      this.store.dispatch(setJobFilter({key: this.userLocationFilterKey, value: {lat: 0, lng: 0}}));

      if(this.isMobileOverlayOpened) {
        const searchHTMLInput = this.searchEl.nativeElement as HTMLInputElement;
        searchHTMLInput.focus({preventScroll: true});
      }
    }
    else this.closeMobileOverlay();

  }

  onFocusOut() {
    setTimeout(() => {
      if(this.display.isMobileScreenSize()) {
        this.actionIconType = 'clear';
      } else {
        // this.actionIconType = this.searchValue.length ? 'clear' : 'submit';
        this.actionIconType = 'submit';
      }
    }, 100);
  }

  showDistrictsWhenDisabled() {
    if(this.typeDisabled) {
      this.showDistrictSuggestions();
    }
  }

  onFocusOutMain($event: FocusEvent) {
    console.log($event);

    const newTarget = $event.relatedTarget as HTMLElement;
    if(this.mainEl.nativeElement) {
      const mainElHTML = this.mainEl.nativeElement as HTMLElement;

      if(!mainElHTML.contains(newTarget)) {
        this.suggestionsType = SuggestionsType.NONE;
      }
    }
  }
}

enum SuggestionsType {
  NONE,
  ADDRESS,
  DISTRICT
}
